import React, { useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useDeviceTags, useTagVocabulary } from '../hooks/useDeviceTags';
import TagEditor from './TagEditor';

/**
 * FS-501 — the shared per-device tag control.
 *
 * Mounted in both places an operator reaches a device: the Device Workspace
 * Overview tab and the Device List row editor. Same component, same rules in
 * both, which is what keeps the creation gate from having two behaviors.
 */
const DeviceTagsPanel: React.FC<{
  deviceId: number;
  className?: string;
  /**
   * Called after a tag is attached or detached, for surfaces that render the
   * device's tags themselves. The Device List passes its search refetch: a tag
   * change alters the row's Tags column, and can move the row in or out of an
   * active tag filter, so without this the table silently disagrees with the
   * editor sitting on top of it.
   */
  onTagsChanged?: () => void;
}> = ({ deviceId, className, onTagsChanged }) => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('write_devices');
  const { tags, loading, error, reload } = useDeviceTags(deviceId);
  const { tags: vocabulary, reload: reloadVocabulary } = useTagVocabulary();

  // Attaching can mint a tag and always changes a count, so both lists refresh
  // — plus whatever the host surface renders from the same data.
  const onChanged = useCallback(() => {
    reload();
    reloadVocabulary();
    onTagsChanged?.();
  }, [reload, reloadVocabulary, onTagsChanged]);

  return (
    <div className={className}>
      {error && <p className="text-xs text-red-700 mb-1">{error}</p>}
      {loading && tags.length === 0 ? (
        <p className="text-xs text-muted-foreground">Loading tags…</p>
      ) : (
        <TagEditor
          deviceId={deviceId}
          tags={tags}
          vocabulary={vocabulary}
          onChanged={onChanged}
          canEdit={canEdit}
        />
      )}
    </div>
  );
};

export default DeviceTagsPanel;
